import { useState, useCallback, useMemo } from 'react';
import type { FieldValidation, ValidationRule } from '@/utils/validation';
import { validateField, validateFields, hasErrors } from '@/utils/validation';

export interface UseFormValidationReturn {
  values: Record<string, string>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  setValue: (field: string, value: string) => void;
  setFieldTouched: (field: string) => void;
  setAllTouched: () => void;
  validateForm: () => boolean;
  isValid: boolean;
  resetForm: () => void;
}

export const useFormValidation = (
  initialValues: Record<string, string>,
  validationRules: Record<string, ValidationRule[]>
): UseFormValidationReturn => {
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Validate a single field
  const validateSingleField = useCallback(
    (field: string, value: string) => {
      const rules = validationRules[field];
      if (!rules) return null;
      return validateField(value, rules);
    },
    [validationRules]
  );

  // Set field value and validate
  const setValue = useCallback(
    (field: string, value: string) => {
      setValues((prev) => ({ ...prev, [field]: value }));

      // Validate immediately if field was touched
      if (touched[field]) {
        const error = validateSingleField(field, value);
        setErrors((prev) => {
          if (error) {
            return { ...prev, [field]: error };
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [field]: _, ...rest } = prev;
            return rest;
          }
        });
      }
    },
    [touched, validateSingleField]
  );

  // Mark field as touched
  const setFieldTouched = useCallback(
    (field: string) => {
      setTouched((prev) => ({ ...prev, [field]: true }));

      // Validate on blur
      const error = validateSingleField(field, values[field] || '');
      if (error) {
        setErrors((prev) => ({ ...prev, [field]: error }));
      }
    },
    [validateSingleField, values]
  );

  // Mark all fields as touched
  const setAllTouched = useCallback(() => {
    const allTouched = Object.keys(validationRules).reduce(
      (acc, key) => {
        acc[key] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setTouched(allTouched);
  }, [validationRules]);

  // Validate entire form
  const validateForm = useCallback((): boolean => {
    const fields: Record<string, FieldValidation> = {};

    Object.keys(validationRules).forEach((key) => {
      fields[key] = {
        value: values[key] || '',
        rules: validationRules[key],
      };
    });

    const validationErrors = validateFields(fields);
    setErrors(validationErrors);
    setAllTouched();

    return !hasErrors(validationErrors);
  }, [values, validationRules, setAllTouched]);

  // Check if form is valid
  const isValid = useMemo(() => {
    const fields: Record<string, FieldValidation> = {};

    Object.keys(validationRules).forEach((key) => {
      fields[key] = {
        value: values[key] || '',
        rules: validationRules[key],
      };
    });

    const validationErrors = validateFields(fields);
    return !hasErrors(validationErrors);
  }, [values, validationRules]);

  // Reset form
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setTouched({});
    setErrors({});
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    setValue,
    setFieldTouched,
    setAllTouched,
    validateForm,
    isValid,
    resetForm,
  };
};
